import TopBar from "../../../common/TopBar";
import ActionBar from "../../../common/ActionBar";
import CustomizationForm from "./children/CustomizationForm";
import PlanManagerModal from "../../../common/PlanManagerModal";
import PreviewPanel from "./children/PreviewPanel";
import { useCustom } from "./useCustom";
import { SHOW_PREVIEW, SITE_CUSTOMIZATION_TOAST_MESSAGES } from "../../../common/constants";
import { FORM_BLOCKER_COPY, PRE_ENROLLMENT_CUSTOM_LIST_ROUTE } from "./useCustom";
import "./Custom.scss";

/**
 * Pre-enrollment site customization editor — presentational shell delegating state
 * to {@link useCustom}.
 *
 * @returns {import('react').JSX.Element}
 */
const PreEnrollmentCustomCustom = () => {
    const {
        clientName,
        data,
        isPublished,
        saveError,
        saveSuccess,
        publishModal,
        enabledFields,
        errors,
        isPublishDisabled,
        templateTitleOptions,
        selectedTemplateTitle,
        publishModalCopy,
        handleTemplateChange,
        handleFieldChange,
        handleListItemChange,
        handleCentivoCareChange,
        handleImageChange,
        handleSave,
        handlePublishClick,
        handleConfirmPublish,
        handleCloseModal,
        isLoading,
        isError,
        loadErrorMessage,
    } = useCustom();

    const showEditor = !isLoading && !isError;

    return (
        <div className="site-customization-page">
            <TopBar variant="detail">
                <TopBar.Back
                    to={PRE_ENROLLMENT_CUSTOM_LIST_ROUTE}
                    label="Pre-enrollment custom"
                    ariaLabel="Back to pre-enrollment custom"
                />
                <TopBar.Title>
                    Site customization{clientName ? ` — ${clientName}` : ""}
                </TopBar.Title>
            </TopBar>

            {isLoading && (
                <p className="site-customization-page__loading" role="status">
                    Loading site customization...
                </p>
            )}

            {isError && (
                <p className="site-customization-page__error" role="alert">
                    {loadErrorMessage}
                </p>
            )}

            {showEditor ? (
                <div className="site-customization-page__layout">
                    <div className="site-customization-page__form-column">
                        {isPublished ? (
                            <div
                                className="site-customization-page__form-blocker"
                                role="note"
                            >
                                {FORM_BLOCKER_COPY}
                            </div>
                        ) : null}
                        <CustomizationForm
                            data={data}
                            enabledFields={enabledFields}
                            errors={errors}
                            templateTitleOptions={templateTitleOptions}
                            selectedTemplateTitle={selectedTemplateTitle}
                            onTemplateChange={handleTemplateChange}
                            onFieldChange={handleFieldChange}
                            onListItemChange={handleListItemChange}
                            onCentivoCareChange={handleCentivoCareChange}
                            onImageChange={handleImageChange}
                        />
                    </div>

                    <div className="site-customization-page__preview-column">
                        <ActionBar
                            layout="end"
                            className="site-customization-page__toolbar"
                            alerts={[
                                ...(saveError
                                    ? [
                                          {
                                              tone: "error",
                                              message:
                                                  SITE_CUSTOMIZATION_TOAST_MESSAGES.SAVE_ERROR,
                                          },
                                      ]
                                    : []),
                                ...(saveSuccess
                                    ? [
                                          {
                                              tone: "success",
                                              message:
                                                  SITE_CUSTOMIZATION_TOAST_MESSAGES.SAVE_SUCCESS,
                                          },
                                      ]
                                    : []),
                            ]}
                            actions={[
                                {
                                    label: "Save",
                                    variant: "accent",
                                    onClick: handleSave,
                                },
                                {
                                    label: isPublished ? "Unpublish" : "Publish",
                                    variant: "outline-accent",
                                    disabled: isPublishDisabled && !isPublished,
                                    onClick: handlePublishClick,
                                },
                            ]}
                        />
                        <PreviewPanel
                            showPreview={SHOW_PREVIEW}
                            clientName={clientName}
                            isPublished={isPublished}
                            data={data}
                            enabledFields={enabledFields}
                        />
                    </div>
                </div>
            ) : null}

            {publishModal.isOpen && publishModalCopy ? (
                <PlanManagerModal
                    isOpen
                    title={
                        publishModal.isSuccess ? "Success" : publishModalCopy.title
                    }
                    body={publishModalCopy.text}
                    primaryText={
                        publishModal.isSuccess
                            ? "Close"
                            : publishModalCopy.confirmButtonText
                    }
                    onPrimary={
                        publishModal.isSuccess
                            ? handleCloseModal
                            : handleConfirmPublish
                    }
                    secondaryText={
                        publishModal.isSuccess
                            ? undefined
                            : publishModalCopy.secondaryButtonText
                    }
                    onSecondary={
                        publishModal.isSuccess ? undefined : handleCloseModal
                    }
                    onClose={
                        publishModal.isSuccess ? handleCloseModal : undefined
                    }
                />
            ) : null}
        </div>
    );
};

export default PreEnrollmentCustomCustom;
